import { TCG_IN_MMM, convertContainerExtWCgsToCellInfo, createRowsFromConfig, generateCoordFunctions, } from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, CraneSideEnum, VesselPartTypeEnum, } from "open-vessel-definition";
import { assertNonNullish, feetToMillimeters, roundDec, sanitizeString, } from "@tedivo/tedivo-pure-helpers";
import { colorShade } from "../helpers/colorShade";
import { createSvgText } from "../helpers/createSvgText";
import { createSvgTopBayNumbers } from "../helpers/createSvgTopBayNumbers";
import { generateTopContainerSymbols } from "./generateTopContainerSymbols";
import { lidDataForBaysValidatorFn } from "@tedivo/tedivo-ovd-validators";
import { obtainFilterFn } from "./filterFns/obtainFilterFn";
export function useSimpleDeckView({ sizeSummary, baysData, masterCGs, lidData, bayLcvVcgTcgAndPairings, symbolsOptions, vesselPartsData, containersWithPositions, svgElement, svgElementMeasures, doDrawVesselParts = true, selectContainerCriteria = "highestBelow", bayNumbersDoubleClickAction, svgGroupId = "createSimpleDeckView-g", }) {
    const { bayLevelPositionsAbove, bayLevelPositionsBelow, missingImportantVcgs, missingImportantLcgs, missingImportantTcgs, lcgsBy20Bay, cgStats: { totalSlotsCount }, } = bayLcvVcgTcgAndPairings;
    const missingImportantXcgs = missingImportantTcgs || missingImportantLcgs || missingImportantVcgs;
    // 1. Add missing LCGs and VCGs if needed, return x,y functions
    const coordFns = generateCoordFunctions({
        sizeSummary,
        baysData,
        vesselPartsData,
        bayLcvVcgTcgAndPairings,
        maxHeightAbsolute: containersWithPositions.maxHeightAbsolute,
        svgElementMeasures,
    });
    assertNonNullish(coordFns, "Could not generate coord functions");
    const { xRange, zRange, xPos, zPos, scaled, addX, zScale, svgSizeRatio, vOneBaySlots, oneInchScaled, allPartsLengthInMeters, } = coordFns;
    // 2. SVG Node
    const svgNode = svgElement.cloneNode(true);
    const svgDeckViewNode = svgNode.querySelector("#DeckView");
    const svgDeckNode = svgNode.querySelector("#Deck");
    if (!svgDeckNode || !svgDeckViewNode)
        throw new Error("No SVG node found with id 'Deck'");
    if (xRange)
        svgNode.setAttribute("width", String(roundDec(xRange, 0)));
    if (zRange)
        svgNode.setAttribute("height", String(roundDec(zRange, 0)));
    if (xRange && zRange) {
        svgNode.setAttribute("viewBox", `0 0 ${roundDec(xRange, 0)} ${roundDec(zRange, 0)}`);
    }
    adjustSvg(svgDeckNode);
    const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgGroup.setAttribute("id", svgGroupId);
    // 3. Add symbols
    const symbols = generateTopContainerSymbols(symbolsOptions || {}, svgSizeRatio);
    Object.keys(symbols).forEach((len) => {
        svgGroup.appendChild(symbols[len]);
    });
    // 4. Draw Vessel Parts
    drawVesselParts(doDrawVesselParts);
    // 5. Top bay numbers
    const doNotRepeatBays = new Set();
    const drawTopBayNumbers = (blps) => createSvgTopBayNumbers({
        blps,
        lcgsBy20Bay,
        svgNode: svgGroup,
        xPos,
        fontColor: symbolsOptions && symbolsOptions.fontColor,
        fontSize: 32,
        onDoubleClick: bayNumbersDoubleClickAction,
        doNotRepeatBays,
    });
    let validStats = {
        ok: 0,
        warning: 0,
        danger: 0,
    };
    if (totalSlotsCount > 0) {
        // 7. Draw containers
        drawDeckContainers(containersWithPositions, selectContainerCriteria);
        // 8. Draw Lids
        const validStatsRet = drawLids(lcgsBy20Bay);
        if (validStatsRet)
            validStats = validStatsRet;
    }
    if (bayLevelPositionsAbove.length)
        drawTopBayNumbers(bayLevelPositionsAbove);
    else
        drawTopBayNumbers(bayLevelPositionsBelow);
    svgNode.appendChild(svgGroup);
    return {
        deckViewSvg: svgNode,
        missingImportantXcgs,
        xRange,
        zRange,
        xPos,
        zPos,
        lcgsBy20Bay,
        addX,
        validStats,
    };
    function adjustSvg(svgDeckNode) {
        svgNode.setAttribute("data-scale", String(roundDec(svgSizeRatio, 5)));
        svgNode.setAttribute("data-one-feet-scaled", String(oneInchScaled));
        svgNode.setAttribute("data-length-meters", String(allPartsLengthInMeters));
        svgDeckNode.setAttribute("id", `${svgGroupId}-DeckView`);
        svgDeckNode.style.transformOrigin = "0 0";
        svgDeckNode.style.transformBox = "fill-box";
        svgDeckNode.style.overflow = "visible";
        svgDeckNode.setAttribute("transform", `matrix(1, 0, 0, ${zScale}, 0, ${svgElementMeasures.paddingShip.z})`);
        // Adjust colors
        if (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipTopColor) {
            const cTop = svgNode.querySelector("#_Deck_LinearBottom");
            if (cTop) {
                const offsets = Array.from(cTop.querySelectorAll("stop[offset]"));
                const color0 = symbolsOptions.shipTopColor;
                const color1 = colorShade(color0, 60);
                const off0 = offsets.find((o) => o.getAttribute("offset") === "0");
                const off1 = offsets.find((o) => o.getAttribute("offset") === "1");
                if (off0)
                    off0.style.stopColor = color0;
                if (off1)
                    off1.style.stopColor = color1;
            }
        }
    }
    function drawDeckContainers(containersWithPositions, selectContainerCriteria) {
        /** Get the filter function to use to filter the containers */
        const getContsFilterFn = obtainFilterFn(selectContainerCriteria);
        const filteredContainers = containersWithPositions.smartCGs.smartBayBlocks
            .map(getContsFilterFn)
            .filter((c) => c.containers.length > 0).map((c) => (Object.assign(Object.assign({}, c), { containers: c.containers.map(convertContainerExtWCgsToCellInfo) })));
        const allContainers = filteredContainers.flatMap((c) => c.containers);
        allContainers.forEach((c) => {
            if (c.lcg === undefined ||
                c.tcg === undefined ||
                c.meta === undefined ||
                c.bottomTier === undefined)
                return;
            const x = xPos(c.lcg, 0);
            const y = zPos(c.tcg, 0);
            const isAbove = c.meta.level === BayLevelEnum.ABOVE;
            const contSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");
            contSvg.setAttribute("href", `#dmbCn${c.size}`);
            contSvg.setAttribute("x", `${x}`);
            contSvg.setAttribute("y", `${y}`);
            if (isAbove && (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.dimmedStrokeColor)) {
                contSvg.setAttribute("stroke", symbolsOptions.dimmedStrokeColor);
                contSvg.setAttribute("stroke-opacity", "0.33");
            }
            contSvg.setAttribute("data-p", `${c.isoOddBay}-${c.meta.row}`);
            contSvg.setAttribute("data-l", `${c.meta.level}`);
            svgGroup.appendChild(contSvg);
            if (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.addRowLines) {
                const textColor = (c.meta.row === "00"
                    ? symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.fontColor
                    : Number(c.meta.row) % 2 === 0
                        ? symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.portColor
                        : symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.stbdColor) || (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.fontColor);
                const stckNumber = createSvgText({
                    text: c.meta.row,
                    x: 0,
                    y: 0,
                    fontSize: 21,
                    textColor: !isAbove ? textColor : (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.dimmedStrokeColor) || "#ddd",
                });
                stckNumber.setAttribute("transform", `rotate(90, 0,0) translate(${y}, ${-x} )`);
                svgGroup.appendChild(stckNumber);
            }
        });
    }
    function drawLids(lcgsBy20Bay) {
        const halfDepthInMilimeters = roundDec(TCG_IN_MMM * 0.5, 1); // Doesn't use svgSizeRatio because xPos is applied later
        if (sizeSummary.maxRow === undefined)
            return;
        const { bayLevelPositionsBelow, bayLevelPositionsAbove } = bayLcvVcgTcgAndPairings;
        const validStats = {
            ok: 0,
            warning: 0,
            danger: 0,
        };
        // Validate lids
        const { result: { rowTcgsByBay, allLids: lidsDataWithValidation }, } = lidDataForBaysValidatorFn({
            data: {
                json: { sizeSummary, lidData },
                lcgVcgTcgAndPairings: bayLcvVcgTcgAndPairings,
                lcgsBy20Bay,
            },
        });
        /** Make a calculation of all the TCGs in the ship to use if missing (lids over non-existing rows in bay) */
        const tempMasterAllTcgs = {};
        if (!missingImportantTcgs) {
            [...bayLevelPositionsBelow, ...bayLevelPositionsAbove].forEach((blp) => {
                const rows = Object.keys(blp.perRowAverageBayTcg).sort();
                rows.forEach((row) => {
                    const maxTcg = Math.abs(tempMasterAllTcgs[row] || 0);
                    const tcg = blp.perRowAverageBayTcg.get(row);
                    if (maxTcg < Math.abs(tcg || 0))
                        tempMasterAllTcgs[row] = tcg || 0;
                });
            });
        }
        else {
            const rows = createRowsFromConfig(!!sizeSummary.centerLineRow, sizeSummary.maxRow);
            rows.forEach((row) => {
                const maxTcg = Math.abs(tempMasterAllTcgs[row] || 0);
                const tcg = bayLcvVcgTcgAndPairings.getMissingTcg(row);
                if (maxTcg < Math.abs(tcg || 0))
                    tempMasterAllTcgs[row] = tcg || 0;
            });
        }
        // Draw lids
        lidsDataWithValidation
            .filter((lid) => lcgsBy20Bay[lid.startIsoBay] !== undefined && lcgsBy20Bay[lid.endIsoBay] !== undefined)
            .forEach((lid, idx) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            const lcgs20StartIsoBay = (_b = (_a = lcgsBy20Bay[lid.startIsoBay].lcgs) === null || _a === void 0 ? void 0 : _a.get) === null || _b === void 0 ? void 0 : _b.call(_a, 20);
            const lcgs20EndIsoBay = (_d = (_c = lcgsBy20Bay[lid.endIsoBay].lcgs) === null || _c === void 0 ? void 0 : _c.get) === null || _d === void 0 ? void 0 : _d.call(_c, 20);
            if (lcgs20StartIsoBay === undefined || lcgs20EndIsoBay === undefined)
                return;
            const foreLcg = lcgs20StartIsoBay.foreLcg;
            const aftLcg = lcgs20EndIsoBay.aftLcg;
            let portTcg = (_k = (_j = (_h = (_f = (_e = rowTcgsByBay[lid.startIsoBay]) === null || _e === void 0 ? void 0 : _e[lid.portIsoRow]) !== null && _f !== void 0 ? _f : (_g = rowTcgsByBay[lid.endIsoBay]) === null || _g === void 0 ? void 0 : _g[lid.portIsoRow]) !== null && _h !== void 0 ? _h : tempMasterAllTcgs[lid.portIsoRow]) !== null && _j !== void 0 ? _j : masterCGs.belowTcgs[lid.portIsoRow]) !== null && _k !== void 0 ? _k : masterCGs.aboveTcgs[lid.portIsoRow];
            let stbdTcg = (_r = (_q = (_p = (_m = (_l = rowTcgsByBay[lid.startIsoBay]) === null || _l === void 0 ? void 0 : _l[lid.starboardIsoRow]) !== null && _m !== void 0 ? _m : (_o = rowTcgsByBay[lid.endIsoBay]) === null || _o === void 0 ? void 0 : _o[lid.starboardIsoRow]) !== null && _p !== void 0 ? _p : tempMasterAllTcgs[lid.starboardIsoRow]) !== null && _q !== void 0 ? _q : masterCGs.belowTcgs[lid.starboardIsoRow]) !== null && _r !== void 0 ? _r : masterCGs.aboveTcgs[lid.starboardIsoRow];
            if (foreLcg !== undefined &&
                aftLcg !== undefined &&
                portTcg !== undefined &&
                stbdTcg !== undefined) {
                portTcg -= halfDepthInMilimeters;
                stbdTcg += halfDepthInMilimeters;
                const lidColor = lid.notValid === "danger"
                    ? (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidDangerFillColor) || "red"
                    : lid.notValid === "warning"
                        ? (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidWarningFillColor) || "yellow"
                        : (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidFillColor) || "black";
                if (lid.notValid !== undefined)
                    validStats[lid.notValid]++;
                else
                    validStats.ok++;
                const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                path.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                path.setAttribute("stroke-width", String(((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 4) * 1.5));
                path.setAttribute("fill", lidColor);
                path.setAttribute("fill-opacity", "0.4");
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                path.setAttribute("data-idx", lid.pk || String(idx));
                path.setAttribute("data-z", String(Math.round((xPos(aftLcg) + xPos(foreLcg)) * 0.5)));
                path.setAttribute("data-x", String(Math.round((zPos(portTcg) + zPos(stbdTcg)) * 0.5)));
                path.setAttribute("d", `M${xPos(aftLcg, 2)},${zPos(portTcg)}
              L${xPos(foreLcg, 2)},${zPos(portTcg)}
              L${xPos(foreLcg, 2)},${zPos(stbdTcg)}
              L${xPos(aftLcg, 2)},${zPos(stbdTcg)}
              z`);
                const xText = xPos((aftLcg + foreLcg) * 0.5, 2), yText = zPos((portTcg + stbdTcg) * 0.5, 2);
                const lidLabel = sanitizeString(lid.label);
                const title = createSvgText({
                    text: lidLabel,
                    x: 0,
                    y: (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.addRowLines) ? 4 : 0,
                    textColor: (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidTextColor) || "red",
                    fontSize: 32,
                });
                title.setAttribute("transform", `rotate(90, 0,0) translate(${yText}, ${-xText} )`);
                title.setAttribute("font-family", "Arial, Helvetica, sans-serif");
                if (lid.overlapPort) {
                    const overlapPort = document.createElementNS("http://www.w3.org/2000/svg", "line");
                    overlapPort.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                    overlapPort.setAttribute("stroke-width", String(((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1) * 4));
                    overlapPort.setAttribute("x1", String(xPos(aftLcg, 2)));
                    overlapPort.setAttribute("y1", String(zPos(portTcg, 2)));
                    overlapPort.setAttribute("x2", String(xPos(foreLcg, 2)));
                    overlapPort.setAttribute("y2", String(zPos(portTcg, 2)));
                    overlapPort.setAttribute("stroke-linejoin", "round");
                    svgGroup.appendChild(overlapPort);
                }
                if (lid.overlapStarboard) {
                    const overlapStbd = document.createElementNS("http://www.w3.org/2000/svg", "line");
                    overlapStbd.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                    overlapStbd.setAttribute("stroke-width", String(((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1) * 4));
                    overlapStbd.setAttribute("x1", String(xPos(aftLcg, 2)));
                    overlapStbd.setAttribute("y1", String(zPos(stbdTcg, 2)));
                    overlapStbd.setAttribute("x2", String(xPos(foreLcg, 2)));
                    overlapStbd.setAttribute("y2", String(zPos(stbdTcg, 2)));
                    svgGroup.appendChild(overlapStbd);
                }
                svgGroup.appendChild(path);
                svgGroup.appendChild(title);
            }
        });
        return validStats;
    }
    function drawVesselParts(doDrawVesselParts) {
        if (!doDrawVesselParts)
            return;
        vOneBaySlots.forEach((vOneBay) => {
            if (vOneBay.type === VesselPartTypeEnum.BAY || vOneBay.type === VesselPartTypeEnum.SPACER) {
                return;
            }
            const x = xPos(vOneBay.aftLcg, 2);
            switch (vOneBay.type) {
                case "BRG":
                    useSymbolAndTransform({
                        id: `#_Deck_Bridge`,
                        x: x,
                        y: svgElementMeasures.paddingShip.z,
                        rotate: 0,
                        padX: scaled(250), // 250mm = 0.25m L space between parts
                        padY: svgElementMeasures.deckPadWidth,
                        len: scaled(feetToMillimeters(vOneBay.len)),
                        zScale,
                        objToAppend: svgNode,
                        partNameInMeasures: "bridge",
                    });
                    break;
                case "SMK":
                    useSymbolAndTransform({
                        id: vOneBay.numberOfSmokeStacks > 1
                            ? "#_Deck_SmokeStack2"
                            : `#_Deck_SmokeStack1`,
                        x: x,
                        y: svgElementMeasures.paddingShip.z,
                        rotate: 0,
                        padX: scaled(250), // 250mm = 0.25m L space between parts
                        padY: svgElementMeasures.deckPadWidth,
                        len: scaled(feetToMillimeters(vOneBay.len)),
                        zScale,
                        objToAppend: svgNode,
                        partNameInMeasures: "smokestack",
                    });
                    break;
                case "CRN":
                    vOneBay.cranes.forEach((crane) => {
                        const fromEdgeToShipAreaY = svgElementMeasures.paddingShip.z + svgElementMeasures.deckPadWidth;
                        const attrs = {
                            rotate: crane === CraneSideEnum.PORT ? 0 : crane === CraneSideEnum.STARBOARD ? 180 : 90,
                            x: x +
                                (crane === CraneSideEnum.PORT ? 0 : scaled(feetToMillimeters(vOneBay.len)) * 0.5),
                            y: crane === CraneSideEnum.PORT
                                ? fromEdgeToShipAreaY
                                : crane === CraneSideEnum.STARBOARD
                                    ? zRange - fromEdgeToShipAreaY + 2 * svgElementMeasures.deckPadWidth
                                    : zRange * 0.5,
                            name: crane === CraneSideEnum.PORT ? "port" : "stbd",
                        };
                        useSymbolAndTransform(Object.assign(Object.assign({ id: `#_Deck_Crane` }, attrs), { transformOrigin: "0 0", padX: scaled(250), padY: 0, len: scaled(feetToMillimeters(vOneBay.len)), zScale, objToAppend: svgNode, partNameInMeasures: "crane" }));
                    });
                    break;
            }
        });
    }
    function useSymbolAndTransform({ id, x, y, rotate, padX, padY, len, zScale, objToAppend, transformOrigin = "0 0", partNameInMeasures, name, }) {
        var _a;
        const elDef = svgElement.querySelector(id);
        if (!elDef)
            throw new Error(`No SVG node found with id '${id}'`);
        const el = (_a = elDef.querySelector("g")) === null || _a === void 0 ? void 0 : _a.cloneNode(true);
        if (!el)
            throw new Error(`No SVG node G found within id '${id}'`);
        const xScale = (len - padX * 2) / svgElementMeasures.parts[partNameInMeasures].length;
        const adjustedZScale = (svgElementMeasures.parts[partNameInMeasures].width /
            (svgElementMeasures.parts[partNameInMeasures].width + svgElementMeasures.deckPadWidth)) *
            zScale;
        el.style.transformOrigin = transformOrigin;
        el.style.transformBox = "fill-box";
        el.style.overflow = "visible";
        el.setAttribute("transform", ` translate(${x + padX}, ${y + padY}) rotate(${rotate}) scale(${xScale}, ${adjustedZScale}) `);
        // IN CASE IT'S NEEDED FOR Server-Render
        // el.style.transform = ` translate(${x + padX}, ${
        //   y + padY
        // }) rotate(${rotate}) scale(${xScale}, ${adjustedZScale}) `;
        if (name) {
            el.setAttribute("data-name", name);
        }
        // const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        // rect.setAttribute("x", `${x}`);
        // rect.setAttribute("y", `${y}`);
        // rect.setAttribute("width", `${len}`);
        // rect.setAttribute(
        //   "height",
        //   `${svgElementMeasures.parts.bridge.width * adjustedZScale}`,
        // );
        // rect.setAttribute("fill", "#ff0000");
        // rect.setAttribute("fill-opacity", "0.5");
        objToAppend.appendChild(el);
        // objToAppend.appendChild(rect);
    }
}
